// src/components/sections/Clientes/Clientes.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import "./clientes.css";
import ImgTitle from "../../layout/ImgTitle/ImgTitle";
import Loader from "../../elements/Loader/Loader";
import useStore from "../../../useStore";
import { useToast } from "../../../Contexts/Toast/toast.provider";

const Clientes = () => {
  // — Carousel state —
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleSlides, setVisibleSlides] = useState(5);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const refContainer = useRef(null);

  // — Store & Toast —
  const homes = useStore(state => state.homes);
  const loading = useStore(state => state.loading);
  const error = useStore(state => state.error);
  const fetchHomes = useStore(state => state.fetchHomes);
  const showToast = useToast();

  // 1) Asegurarnos de tener homes cargados
  useEffect(() => {
    if (!homes.length) {
      fetchHomes();
    }
  }, [homes.length, fetchHomes]);

  // 2) Mostrar toast en caso de error
  useEffect(() => {
    if (error) {
      showToast("error", `❌ Error al cargar clientes: ${error}`);
    }
  }, [error, showToast]);

  // 3) Derivar clientes y banners de `homes`
  const { clientes, bannerDesktop, bannerMobile } = useMemo(() => {
    if (!Array.isArray(homes) || homes.length === 0) {
      return { clientes: [], bannerDesktop: "", bannerMobile: "" };
    }
    const home = homes.find(item => item.clientesContent) || {};
    const list = home.clientesContent?.clientesData || [];
    const desk = home.clientesContent?.imagenBannerEscritorio?.url || "";
    const mob = home.clientesContent?.imagenBannerMobile?.url || "";
    const base = process.env.REACT_APP_STRAPI_API_BASE.replace("/api", "");
    return {
      clientes: list,
      bannerDesktop: `${base}${desk}`,
      bannerMobile: `${base}${mob}`,
    };
  }, [homes]);

  // 4) Ajustar número de slides según ancho
  useEffect(() => {
    const updateLayout = () => {
      if (!refContainer.current) return;
      const w = refContainer.current.offsetWidth;
      setContainerWidth(w);
      if (w <= 767) setVisibleSlides(1);
      else if (w <= 1023) setVisibleSlides(3);
      else setVisibleSlides(4);
    };
    window.addEventListener("resize", updateLayout);
    updateLayout();
    return () => window.removeEventListener("resize", updateLayout);
  }, []);

  // 5) Auto-scroll
  useEffect(() => {
    if (isPaused || clientes.length <= visibleSlides) return;
    const max = Math.max(0, clientes.length - visibleSlides);
    const iv = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % (max + 1));
    }, 3000);
    return () => clearInterval(iv);
  }, [visibleSlides, isPaused, clientes]);

  // 6) Helpers de estilo
  const getTransform = () => {
    const spacing = 20;
    const slideW = containerWidth / visibleSlides;
    return `translateX(-${currentIndex * (slideW + spacing)}px)`;
  };
  const getTrackWidth = () => {
    const spacing = 20;
    const slideW = containerWidth / visibleSlides;
    return clientes.length * slideW + (clientes.length - 1) * spacing;
  };

  // 7) Loader/fallback
  if (loading) return <Loader />;
  if (!clientes.length) return null;

  return (
    <>
      <ImgTitle
        desktopImage={bannerDesktop}
        mobileImage={bannerMobile}
      />

      <div className="container nuestros-container">
        <div className="carousel carousel-clientes" ref={refContainer}>
          <div className="carousel-track-container">
            <ul
              className="carousel-track"
              style={{
                transform: getTransform(),
                width: `${getTrackWidth()}px`,
              }}
            >
              {clientes.map((cliente, i) => {
                const slideW = containerWidth / visibleSlides;
                const imgUrl = cliente.clienteImagen?.url
                  ? `${process.env.REACT_APP_STRAPI_API_BASE.replace("/api", "")}${cliente.clienteImagen.url}`
                  : "";
                return (
                  <li
                    key={i}
                    className="carousel-slide carousel-slide-clientes"
                    style={{
                      width: `${slideW}px`,
                      marginRight: "20px",
                    }}
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                  >
                    <a
                      href={cliente.clienteLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <div
                        className="carousel-image"
                        style={{ backgroundImage: `url(${imgUrl})` }}
                      />
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default Clientes;
